/**
 * Zone « compte » de l'en-tête : notifications (super admin), rôle, nom,
 * avatar et menu profil / déconnexion — extrait de Layout.tsx.
 */
import React, { useState } from 'react';
import { Avatar, Box, Chip, ChipProps, Divider, IconButton, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import { AccountCircle, Logout, Settings } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useRBAC } from '../../contexts/RBACContext';
import { UserRole, ROLE_LABELS } from '../../types/rbac';
import { AdminNotificationsPanel } from './AdminNotificationsPanel';
import { logger } from '../../lib/logger';

/** Taille de l'avatar (px). */
const AVATAR_SIZE = 32;

/** Couleur de la pastille de rôle. */
const ROLE_CHIP_COLOR: Partial<Record<UserRole, ChipProps['color']>> = {
  [UserRole.SUPER_ADMIN]: 'error',
  [UserRole.ADMIN]: 'warning',
};

/** Compte connecté (rien sans session). */
const UserMenu: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user, logout } = useAuth();
  const { claims } = useRBAC();
  const navigate = useNavigate();
  const handleClose = () => setAnchorEl(null);
  const handleProfile = () => { navigate('/profile'); handleClose(); };
  const handleLogout = async () => {
    try { await logout(); navigate('/'); } catch (e) { logger.error('[UserMenu][logout]', e); }
    handleClose();
  };
  if (!user) return null;

  return (
    <Box display="flex" alignItems="center" gap={1}>
      {claims?.role === UserRole.SUPER_ADMIN && <AdminNotificationsPanel />}
      {claims && (
        <Chip label={ROLE_LABELS[claims.role]} size="small" color={ROLE_CHIP_COLOR[claims.role] ?? 'default'} sx={{ mr: 1 }} />
      )}
      <Typography variant="body2">{user.displayName}</Typography>
      <IconButton size="large" onClick={(event) => setAnchorEl(event.currentTarget)} color="inherit">
        <Avatar src={user.photoURL || undefined} alt={user.displayName || 'User'} sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={handleProfile}>
          <ListItemIcon><AccountCircle fontSize="small" /></ListItemIcon>
          Profil
        </MenuItem>
        <MenuItem onClick={handleClose}>
          <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
          Paramètres
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
          Déconnexion
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default UserMenu;
